import { apiClient, ApiError, UnauthorizedError, ForbiddenError } from '@/lib/apiClient';
import { clearSessionAndRedirect } from '@/lib/sessionManager';
import { API_URL, API_KEY } from '@/config/appConfig';

interface ChatRequestBody {
  user_message: string;
  conversation_id?: string;
  last_message_id?: string;
}

export interface ChatApiResponse {
  conversation_id: string;
  last_message_id: string;
  answer?: string;
  response?: string;
  conversation_token_count?: number;
  exhausted?: boolean;
}

export { ApiError as ChatError } from '@/lib/apiClient';

const buildRequestBody = (
  message: string,
  conversationId?: string | null,
  lastMessageId?: string | null,
): ChatRequestBody => {
  const body: ChatRequestBody = { user_message: message };

  if (conversationId) {
    body.conversation_id = conversationId;
  }

  if (lastMessageId) {
    body.last_message_id = lastMessageId;
  }

  return body;
};

const validateChatResponse = (response: ChatApiResponse, hasConversationId: boolean) => {
  if (!hasConversationId && !response.conversation_id) {
    throw new ApiError('No conversation_id received in first response', 500, 'NO_CONVERSATION_ID');
  }

  if (!response.answer && !response.response) {
    throw new ApiError('No answer in response', 500, 'NO_ANSWER');
  }

  if (!response.last_message_id) {
    throw new ApiError('No last_message_id in response', 500, 'NO_LAST_MESSAGE_ID');
  }
};

const handleStreamErrorStatus = async (response: Response): Promise<never> => {
  let detail = `HTTP error! Status: ${response.status}`;
  try {
    const data = await response.json() as Record<string, unknown>;
    detail = (data.detail as string) || (data.message as string) || detail;
  } catch { /* ignore */ }

  if (response.status === 401) {
    clearSessionAndRedirect('Session expired or invalid (401 Unauthorized)');
    throw new UnauthorizedError(detail);
  }
  if (response.status === 403) {
    throw new ForbiddenError(detail);
  }
  throw new ApiError(detail, response.status);
};

export const chatService = {
  sendMessage: async (
    message: string,
    conversationId: string | null = null,
    lastMessageId?: string | null,
    accessToken?: string,
  ): Promise<ChatApiResponse> => {
    try {
      const data = await apiClient<ChatApiResponse>('/assistant/conversation', {
        method: 'POST',
        body: buildRequestBody(message, conversationId, lastMessageId),
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
        skipAuthErrorHandling: true,
      });

      validateChatResponse(data, Boolean(conversationId));
      return data;
    } catch (error) {
      console.error('[ChatService] Error sending message:', error);
      throw error;
    }
  },

  sendStreamingMessage: async (
    message: string,
    conversationId: string | null,
    lastMessageId: string | null | undefined,
    accessToken: string,
    onText: (chunk: string) => void,
    onStatus: (status: string) => void,
    onQueued: (message: string) => void,
  ): Promise<ChatApiResponse> => {
    const response = await fetch(`${API_URL}/assistant/streaming_response`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'API-KEY': API_KEY,
      },
      body: JSON.stringify(buildRequestBody(message, conversationId, lastMessageId)),
    });

    if (!response.ok) {
      await handleStreamErrorStatus(response);
    }

    // Exhausted-conversation fallback: backend returns plain JSON (not a stream)
    const contentType = response.headers.get('Content-Type') ?? '';
    if (contentType.includes('application/json')) {
      const data = await response.json() as ChatApiResponse;
      validateChatResponse(data, Boolean(conversationId));
      return data;
    }

    if (!response.body) {
      throw new ApiError('No response body received from streaming endpoint', 500, 'NO_BODY');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        // Keep the last (potentially incomplete) line in the buffer
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);

          if (data.startsWith('__FINAL_RESPONSE__:')) {
            const jsonStr = data.slice('__FINAL_RESPONSE__:'.length);
            const finalData = JSON.parse(jsonStr) as ChatApiResponse;
            validateChatResponse(finalData, Boolean(conversationId));
            return finalData;
          }

          if (data.startsWith('❌')) {
            throw new ApiError(data, 500, 'STREAM_ERROR');
          }

          try {
            const parsed = JSON.parse(data) as Record<string, string>;
            if (parsed.queued !== undefined) {
              onQueued(parsed.queued);
            } else if (parsed.text !== undefined) {
              onText(parsed.text);
            } else if (parsed.LLM !== undefined) {
              onStatus(parsed.LLM);
            }
          } catch {
            // Ignore malformed SSE lines
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    throw new ApiError('Stream ended without final response', 500, 'NO_FINAL_RESPONSE');
  },
};
